/**
 * custom.js — SurviveX theme client logic.
 * All logic is scoped and self-cleans. No jQuery, no setInterval polling,
 * no leaky MutationObservers, no setTimeout-based DOM hacks.
 */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 749;
  const isMobile = () => window.innerWidth <= MOBILE_BREAKPOINT;
  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  /* ---------- variant-specs block toggling ---------- */
  function initVariantSpecs() {
    const blocks = document.querySelectorAll('.variant-specs-block');
    if (!blocks.length) return;

    const show = (variantId) => {
      blocks.forEach((b) => {
        b.style.display = b.dataset.variantId === variantId ? 'block' : 'none';
      });
    };

    const current = document.querySelector('[name="id"]')?.value;
    if (current) show(current);

    document.addEventListener('change', (e) => {
      if (e.target && e.target.name === 'id') show(e.target.value);
    });
    document.addEventListener('variant:change', (e) => {
      const id = e.detail?.variant?.id;
      if (id) show(String(id));
    });
  }

  /* ---------- default quantity radio ("1 item") ----------
     Previously a 100ms setInterval loop. Replaced with a single
     MutationObserver that disconnects as soon as it finds the input.
  */
  function initDefaultQuantityRadio() {
    const apply = () => {
      const radio = document.querySelector('input[value="1 item"]');
      if (!radio || radio.checked) return false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    };
    if (apply()) return;
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 5000);
  }

  /* ---------- Mobile accordion content mover ----------
     Collapses 5 separate legacy setTimeouts into one idempotent pass.
     Runs once on DOM ready; re-applies on breakpoint change.
  */
  const MOBILE_MOVES = [
    { source: '.mobile-accordion-content', target: '.pdp-what-inside .accordion__content' },
    { source: '.mobile-accordion-content-two', target: '.pdp-what-make-special .accordion__content' },
    { source: '.mobile-faq-content', target: '.pdp-frequently-asked-questions .accordion__content' },
    { source: '.review__slider_wrapper', target: '.pdp-testimonials details .accordion__content', openDetails: '.pdp-testimonials details' },
    { source: '.mobile-faq-content-2', target: '.pdp-compare-size details .accordion__content', openDetails: '.pdp-compare-size details' },
  ];

  function applyMobileMoves() {
    if (!isMobile()) return;
    MOBILE_MOVES.forEach(({ source, target, openDetails }) => {
      const destination = document.querySelector(target);
      if (!destination) return;
      const items = document.querySelectorAll(source);
      if (!items.length) return;
      items.forEach((el) => {
        if (destination.contains(el)) return;
        destination.appendChild(el);
        el.style.display = 'block';
      });
      if (openDetails) {
        const d = document.querySelector(openDetails);
        if (d) d.setAttribute('open', '');
      }
    });
  }

  /* ---------- hide ninth block for large-first-aid-control variant ---------- */
  function initHideNinth() {
    if (!window.location.href.includes('large-first-aid-control')) return;
    if (!isMobile()) return;

    const apply = () => {
      const blocks = document.querySelectorAll('.mobile-accordion-content');
      if (blocks.length >= 9) {
        blocks[8].style.display = 'none';
        return true;
      }
      return false;
    };
    if (apply()) return;
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 5000);
  }

  /* ---------- "What makes special" mobile auto-open ---------- */
  function initWhatMakesSpecial() {
    const collapsible = document.querySelector('.collapsible-what-makes-special');
    if (!collapsible) return;

    const sync = () => {
      if (isMobile()) {
        if (!collapsible.open) collapsible.open = true;
        document.body.classList.add('what_makes_special');
      } else {
        collapsible.open = false;
        document.body.classList.remove('what_makes_special');
      }
    };
    sync();
    collapsible.addEventListener('toggle', () => {
      if (collapsible.open && isMobile()) {
        document.body.classList.add('what_makes_special');
      } else {
        document.body.classList.remove('what_makes_special');
      }
    });
    window.addEventListener('resize', sync, { passive: true });
  }

  /* ---------- Coupon preserves via checkout click ---------- */
  function initCouponCheckoutInterceptor() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(
        [
          'a[href="/checkout"]',
          'a[href*="/checkout"]',
          'button[name="checkout"]',
          'input[name="checkout"]',
          'button[id*="checkout"]',
          'button[class*="checkout"]',
        ].join(',')
      );
      if (!btn) return;
      const coupon = localStorage.getItem('selectedCoupon');
      if (!coupon) return;
      e.preventDefault();
      e.stopPropagation();
      window.location.href = '/discount/' + encodeURIComponent(coupon) + '?redirect=/checkout';
    });
  }

  /* ---------- Back-to-top button ---------- */
  function initBackToTop() {
    const btn = document.getElementById('back-to-top-button');
    if (!btn) return;
    const toggle = () => {
      const visible = window.scrollY > 300;
      btn.classList.toggle('show', visible);
      if (visible) btn.hidden = false;
    };
    window.addEventListener('scroll', toggle, { passive: true });
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    toggle();
  }

  /* ---------- Klaviyo lazy-loader ----------
     Loads only when: a .klaviyo_form_trigger enters viewport,
     OR on first user interaction, OR on idle after page load.
     Previously loaded eagerly on DOMContentLoaded with console noise.
  */
  function initKlaviyo() {
    const companyId = window.klaviyoCompanyId;
    if (!companyId) return;

    let loaded = false;
    const load = () =>
      new Promise((resolve, reject) => {
        if (loaded || document.querySelector('script[src*="klaviyo.com"]')) {
          loaded = true;
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + encodeURIComponent(companyId);
        script.async = true;
        script.onload = () => {
          loaded = true;
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });

    const openForm = (formId) => {
      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', formId || 'Yjf9Cf']);
    };

    const triggers = document.querySelectorAll('.klaviyo_form_trigger');
    triggers.forEach((t) => {
      t.addEventListener('click', (e) => {
        e.preventDefault();
        load().then(() => openForm(t.dataset.klaviyoFormId));
      });
    });

    if ('IntersectionObserver' in window && triggers.length) {
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((x) => x.isIntersecting)) {
            load();
            io.disconnect();
          }
        },
        { rootMargin: '400px' }
      );
      triggers.forEach((t) => io.observe(t));
    }

    const warmup = () => load();
    ['scroll', 'mousemove', 'touchstart', 'keydown'].forEach((ev) => {
      window.addEventListener(ev, warmup, { once: true, passive: true });
    });

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => load(), { timeout: 8000 });
    } else {
      setTimeout(() => load(), 4000);
    }
  }

  /* ---------- Banner video controls ---------- */
  function initBannerVideoControls() {
    const wrappers = document.querySelectorAll('[data-video-id]');
    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const video = wrapper.querySelector('video');
      const muteBtn = wrapper.parentElement.querySelector('.video-mute-unmute');
      const playBtn = wrapper.parentElement.querySelector('.video-play-pause');

      if (!video || !muteBtn || !playBtn) return;

      muteBtn.addEventListener('click', () => {
        video.muted = !video.muted;
        muteBtn.setAttribute('aria-pressed', video.muted ? 'true' : 'false');
        muteBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        muteBtn.classList.toggle('not-mute');
      });

      playBtn.addEventListener('click', () => {
        const willPlay = video.paused;
        if (willPlay) {
          video.play();
        } else {
          video.pause();
        }
        playBtn.setAttribute('aria-pressed', willPlay ? 'true' : 'false');
        playBtn.setAttribute('aria-label', willPlay ? 'Pause video' : 'Play video');
        playBtn.classList.toggle('paused');
      });
    });
  }

  /* ---------- init ---------- */
  onReady(() => {
    initVariantSpecs();
    initDefaultQuantityRadio();
    applyMobileMoves();
    initHideNinth();
    initWhatMakesSpecial();
    initCouponCheckoutInterceptor();
    initBackToTop();
    initKlaviyo();
    initBannerVideoControls();

    let lastMobile = isMobile();
    window.addEventListener(
      'resize',
      () => {
        const nowMobile = isMobile();
        if (nowMobile !== lastMobile) {
          lastMobile = nowMobile;
          if (nowMobile) applyMobileMoves();
        }
      },
      { passive: true }
    );
  });
})();
